import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp, Info, Check, Award, AlertTriangle, 
         List, Shuffle, Sparkles, GripVertical, X, BarChart, ChevronRight } from 'lucide-react';

const VotingSimulator = () => {
  // Modern color palette
  const colors = {
    primary: '#1E40AF', // Strong blue
    secondary: '#6366F1', // Indigo
    accent: '#EC4899', // Pink
    success: '#10B981', // Emerald green
    warning: '#F59E0B', // Amber
    error: '#EF4444', // Red
    neutral: '#6B7280', // Gray
    background: '#F9FAFB', // Light gray background
    card: '#FFFFFF', // White
    text: '#111827', // Near black
    lightText: '#6B7280', // Medium gray
    border: '#E5E7EB', // Light gray
    neutralLight: '#F3F4F6', // Lighter gray for backgrounds
  };

  // Party data with colors and initial vote shares
  const [parties, setParties] = useState({
    democrats: { 
      name: 'Democrats', 
      color: '#3366CC', 
      shorthand: 'Dem',
      voteShare: 39
    },
    republicans: { 
      name: 'Republicans', 
      color: '#DC3912', 
      shorthand: 'Rep',
      voteShare: 41
    },
    green: { 
      name: 'Green Party', 
      color: '#109618', 
      shorthand: 'Green',
      voteShare: 10
    },
    libertarians: { 
      name: 'Libertarian Party', 
      color: '#FF9900', 
      shorthand: 'Lib',
      voteShare: 10
    },
    socialist: { 
      name: 'Social Democracy Party', 
      color: '#990099', 
      shorthand: 'Soc',
      voteShare: 0
    },
    constitution: { 
      name: 'Constitution Party', 
      color: '#0099C6', 
      shorthand: 'Con',
      voteShare: 0
    }
  });

  // Party candidates data
  const partyCandidates = {
    democrats: [
      { id: 'dem1', name: 'Alexandria Smith', bio: 'Progressive advocate focusing on healthcare reform' },
      { id: 'dem2', name: 'Michael Johnson', bio: 'Moderate with economic development background' },
      { id: 'dem3', name: 'Sophia Rodriguez', bio: 'Education policy specialist' },
      { id: 'dem4', name: 'James Wilson', bio: 'Former mayor with infrastructure expertise' },
      { id: 'dem5', name: 'Olivia Chen', bio: 'Civil rights attorney' }
    ],
    republicans: [
      { id: 'rep1', name: 'William Thompson', bio: 'Business leader focusing on deregulation' },
      { id: 'rep2', name: 'Elizabeth Davis', bio: 'Fiscal conservative with military background' },
      { id: 'rep3', name: 'Robert Miller', bio: 'Agriculture policy expert' },
      { id: 'rep4', name: 'Jennifer White', bio: 'Traditional values advocate' },
      { id: 'rep5', name: 'Thomas Brown', bio: 'National security specialist' }
    ],
    green: [
      { id: 'green1', name: 'Emma Nelson', bio: 'Environmental scientist and activist' },
      { id: 'green2', name: 'David Garcia', bio: 'Renewable energy entrepreneur' },
      { id: 'green3', name: 'Rebecca Kim', bio: 'Climate justice advocate' }
    ],
    libertarians: [
      { id: 'lib1', name: 'Andrew Peterson', bio: 'Civil liberties attorney' },
      { id: 'lib2', name: 'Sarah Martinez', bio: 'Economist advocating minimal government' },
      { id: 'lib3', name: 'Jonathan Lee', bio: 'Privacy rights specialist' }
    ],
    socialist: [
      { id: 'soc1', name: 'Maria Hernandez', bio: 'Labor organizer and workers rights advocate' },
      { id: 'soc2', name: 'Daniel Clark', bio: 'Community housing activist' },
      { id: 'soc3', name: 'Aisha Washington', bio: 'Healthcare for All campaign leader' }
    ],
    constitution: [
      { id: 'con1', name: 'George Williams', bio: 'Constitutional originalist' },
      { id: 'con2', name: 'Patricia Anderson', bio: 'Religious freedom advocate' },
      { id: 'con3', name: 'Richard Taylor', bio: 'States rights proponent' }
    ]
  };

  // Application state
  const [state, setState] = useState({
    // System selection
    activeSystem: 'openlist', // 'openlist' or 'stv'
    currentStep: 'party-support', // 'party-support', 'party-selection', 'candidate-ranking', 'results'
    
    // Open List PR selections
    openList: {
      firstChoice: '',
      secondChoice: '',
      candidateRankings: {},
      results: null,
      totalSeats: 10,
      showComparison: false
    },
    
    // STV selections
    stv: {
      rankedCandidates: [],   // Will store ordered list of candidate objects
      results: null,
      totalSeats: 10,
      showComparison: false
    },
    
    // UI state
    ui: {
      expandedParties: {}, // Track which party cards are expanded
      scrollToId: null,
      openSections: {
        welcome: true,
        explanation: true,
        partySupport: true,
        partySelection: false,
        candidateRanking: false,
        results: false,
        comparison: false
      }
    }
  });

  // Calculate total vote percentage to check if it equals 100%
  const totalVotePercentage = Object.values(parties).reduce((sum, party) => sum + party.voteShare, 0);
  
  // Create a flat array of all candidates with party information for STV
  const allCandidatesForSTV = [];
  Object.keys(parties).forEach(partyId => {
    const party = parties[partyId];
    partyCandidates[partyId].forEach(candidate => {
      allCandidatesForSTV.push({
        ...candidate,
        partyId: partyId,
        partyName: party.name,
        partyColor: party.color
      });
    });
  });

  // Toggle a party's expanded state
  const togglePartyExpanded = (partyId) => {
    setState(prevState => ({
      ...prevState,
      ui: {
        ...prevState.ui,
        expandedParties: {
          ...prevState.ui.expandedParties,
          [partyId]: !prevState.ui.expandedParties[partyId]
        }
      }
    }));
  };

  // Add a candidate to the ranked list for STV
  const addCandidateToRanking = (candidate) => {
    // Skip if already in the list
    if (state.stv.rankedCandidates.some(c => c.id === candidate.id)) {
      return;
    }

    setState(prevState => ({
      ...prevState,
      stv: {
        ...prevState.stv,
        rankedCandidates: [...prevState.stv.rankedCandidates, candidate]
      }
    }));
  };

  // Remove a candidate from the ranked list for STV
  const removeCandidateFromRanking = (candidateId) => {
    setState(prevState => ({
      ...prevState,
      stv: {
        ...prevState.stv,
        rankedCandidates: prevState.stv.rankedCandidates.filter(c => c.id !== candidateId)
      }
    }));
  };

  // Reorder candidates using drag and drop for STV
  const reorderCandidates = (startIndex, endIndex) => {
    setState(prevState => {
      const result = Array.from(prevState.stv.rankedCandidates);
      const [removed] = result.splice(startIndex, 1);
      result.splice(endIndex, 0, removed);
      
      return {
        ...prevState,
        stv: {
          ...prevState.stv,
          rankedCandidates: result
        }
      };
    });
  };

  // Function to handle drag start
  const onDragStart = (e, index) => {
    e.dataTransfer.setData('text/plain', index);
    // Add a class to the dragged item for styling
    e.currentTarget.classList.add('dragging');
  };

  // Function to handle drag over
  const onDragOver = (e, index) => {
    e.preventDefault();
    // Add visual indicator for drop zone
    e.currentTarget.classList.add('drag-over');
  };

  // Function to handle drag leave
  const onDragLeave = (e) => {
    e.currentTarget.classList.remove('drag-over');
  };

  // Function to handle drop
  const onDrop = (e, dropIndex) => {
    const dragIndex = parseInt(e.dataTransfer.getData('text/plain'), 10);
    e.currentTarget.classList.remove('drag-over');
    
    // Only reorder if indexes are different
    if (dragIndex !== dropIndex) {
      reorderCandidates(dragIndex, dropIndex);
    }
  };

  // Function to handle drag end
  const onDragEnd = (e) => {
    e.currentTarget.classList.remove('dragging');
  };

  // Scrolling behavior
  useEffect(() => {
    if (state.ui.scrollToId) {
      const element = document.getElementById(state.ui.scrollToId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
        // Reset scrollToId after scrolling
        setState(prevState => ({
          ...prevState,
          ui: {
            ...prevState.ui,
            scrollToId: null
          }
        }));
      }
    }
  }, [state.ui.scrollToId]);

  // Toggle section visibility
  const toggleSection = (sectionId) => {
    setState(prevState => ({
      ...prevState,
      ui: {
        ...prevState.ui,
        openSections: {
          ...prevState.ui.openSections,
          [sectionId]: !prevState.ui.openSections[sectionId]
        }
      }
    }));
  };

  // Set active voting system
  const setActiveSystem = (system) => {
    setState(prevState => ({
      ...prevState,
      activeSystem: system
    }));
  };

  // Handle party vote share changes
  const handlePartyVoteChange = (partyId, newValue) => {
    // Calculate the difference
    const oldValue = parties[partyId].voteShare;
    const difference = newValue - oldValue;
    
    // Create new parties object
    const newParties = {...parties};
    newParties[partyId].voteShare = newValue;
    
    // Calculate total after this change
    const updatedTotal = Object.values(newParties).reduce((sum, party) => sum + party.voteShare, 0);
    
    // If we're going over 100%, adjust the other parties down proportionally
    if (updatedTotal > 100) {
      const excess = updatedTotal - 100;
      
      // Create a proportional reduction for all other parties
      const otherParties = Object.keys(newParties).filter(pId => pId !== partyId);
      const otherPartiesTotal = otherParties.reduce((sum, pId) => sum + newParties[pId].voteShare, 0);
      
      if (otherPartiesTotal > 0) {
        otherParties.forEach(pId => {
          const proportionalReduction = (newParties[pId].voteShare / otherPartiesTotal) * excess;
          newParties[pId].voteShare = Math.max(0, newParties[pId].voteShare - proportionalReduction);
        });
      }
    }
    
    // Ensure whole numbers
    Object.keys(newParties).forEach(pId => {
      newParties[pId].voteShare = Math.round(newParties[pId].voteShare);
    });
    
    // Final check to ensure exactly 100%
    const finalTotal = Object.values(newParties).reduce((sum, party) => sum + party.voteShare, 0);
    if (finalTotal !== 100) {
      // Find the party with the largest vote share (that's not the one we're adjusting)
      const largestParty = Object.keys(newParties)
        .filter(pId => pId !== partyId)
        .reduce((largest, pId) => 
          newParties[pId].voteShare > newParties[largest].voteShare ? pId : largest, 
          Object.keys(newParties).filter(pId => pId !== partyId)[0] || partyId
        );
      
      // Adjust that party to make the total 100%
      if (largestParty && largestParty !== partyId) {
        newParties[largestParty].voteShare += (100 - finalTotal);
      } else {
        // If we can't adjust another party, adjust the current one
        newParties[partyId].voteShare += (100 - finalTotal);
      }
    }
    
    setParties(newParties);
  };

  // Go to the next step in the flow
  const goToNextStep = () => {
    if (state.currentStep === 'party-support') {
      if (state.activeSystem === 'openlist') {
        setState(prevState => ({
          ...prevState,
          currentStep: 'party-selection',
          ui: {
            ...prevState.ui,
            openSections: {
              ...prevState.ui.openSections,
              partySelection: true
            },
            scrollToId: 'party-selection-section'
          }
        }));
      } else {
        setState(prevState => ({
          ...prevState,
          currentStep: 'candidate-ranking',
          ui: {
            ...prevState.ui,
            openSections: {
              ...prevState.ui.openSections,
              candidateRanking: true
            },
            scrollToId: 'candidate-ranking-section'
          }
        }));
      }
    } else if (state.currentStep === 'party-selection') {
      setState(prevState => ({
        ...prevState,
        currentStep: 'candidate-ranking',
        ui: {
          ...prevState.ui,
          openSections: {
            ...prevState.ui.openSections,
            candidateRanking: true
          },
          scrollToId: 'candidate-ranking-section'
        }
      }));
    }
  };

  // Handle first party choice for Open List PR
  const handleFirstPartyChoice = (partyId) => {
    // If selecting the same as second choice, clear second choice
    const newSecondChoice = 
      state.openList.secondChoice === partyId ? '' : state.openList.secondChoice;
    
    setState(prevState => ({
      ...prevState,
      openList: {
        ...prevState.openList,
        firstChoice: partyId,
        secondChoice: newSecondChoice
      }
    }));
  };

  // Handle second party choice for Open List PR
  const handleSecondPartyChoice = (partyId) => {
    // Can't select same as first choice
    if (partyId === state.openList.firstChoice) return;
    
    setState(prevState => ({
      ...prevState,
      openList: {
        ...prevState.openList,
        secondChoice: partyId
      }
    }));
  };

  // Handle candidate ranking for Open List PR - new click to add implementation
  const handleOpenListCandidateAdd = (partyId, candidate) => {
    setState(prevState => {
      // Get existing rankings
      const partyRankings = prevState.openList.candidateRankings[partyId] || {};
      
      // Check if this candidate is already ranked
      if (partyRankings[candidate.id] !== undefined) {
        return prevState; // Already ranked, do nothing
      }
      
      // Get the next available rank for this party
      const existingRanks = Object.values(partyRankings);
      const nextRank = existingRanks.length > 0 ? Math.max(...existingRanks) + 1 : 1;
      
      // Add the new ranking
      const newRankings = { 
        ...prevState.openList.candidateRankings,
        [partyId]: {
          ...partyRankings,
          [candidate.id]: nextRank
        }
      };
      
      return {
        ...prevState,
        openList: {
          ...prevState.openList,
          candidateRankings: newRankings
        }
      };
    });
  };
  
  // Remove a candidate from Open List PR rankings
  const removeOpenListCandidate = (partyId, candidateId) => {
    setState(prevState => {
      const partyRankings = { ...prevState.openList.candidateRankings[partyId] };
      const removedRank = partyRankings[candidateId];
      
      // Delete this candidate from rankings
      delete partyRankings[candidateId];
      
      // Adjust ranks of other candidates to close the gap
      Object.keys(partyRankings).forEach(cId => {
        if (partyRankings[cId] > removedRank) {
          partyRankings[cId]--;
        }
      });
      
      return {
        ...prevState,
        openList: {
          ...prevState.openList,
          candidateRankings: {
            ...prevState.openList.candidateRankings,
            [partyId]: partyRankings
          }
        }
      };
    });
  };
  
  // Reorder candidates for Open List PR using drag and drop
  const reorderOpenListCandidates = (partyId, startIndex, endIndex) => {
    setState(prevState => {
      const partyRankings = prevState.openList.candidateRankings[partyId] || {};
      
      // Find candidates at these indices
      const candidates = Object.entries(partyRankings)
        .sort((a, b) => a[1] - b[1])
        .map(([id]) => id);
      
      // Validate indices
      if (startIndex < 0 || startIndex >= candidates.length || 
          endIndex < 0 || endIndex >= candidates.length) {
        return prevState;
      }
      
      // Remove candidate from old position and insert at new position
      const [removed] = candidates.splice(startIndex, 1);
      candidates.splice(endIndex, 0, removed);
      
      // Update all ranks
      const newRankings = {};
      candidates.forEach((id, index) => {
        newRankings[id] = index + 1;
      });
      
      return {
        ...prevState,
        openList: {
          ...prevState.openList,
          candidateRankings: {
            ...prevState.openList.candidateRankings,
            [partyId]: newRankings
          }
        }
      };
    });
  };
  
  // Functions for handling Open List PR drag and drop
  const onOpenListDragStart = (e, partyId, index) => {
    e.dataTransfer.setData('text/plain', JSON.stringify({ partyId, index }));
    e.currentTarget.classList.add('dragging');
  };
  
  const onOpenListDragOver = (e) => {
    e.preventDefault();
    e.currentTarget.classList.add('drag-over');
  };
  
  const onOpenListDragLeave = (e) => {
    e.currentTarget.classList.remove('drag-over');
  };
  
  const onOpenListDrop = (e, partyId, dropIndex) => {
    e.preventDefault();
    e.currentTarget.classList.remove('drag-over');
    
    try {
      const { partyId: dragPartyId, index: dragIndex } = JSON.parse(e.dataTransfer.getData('text/plain'));
      
      // Only reorder if from the same party and different positions
      if (dragPartyId === partyId && dragIndex !== dropIndex) {
        reorderOpenListCandidates(partyId, dragIndex, dropIndex);
      }
    } catch (err) {
      console.error('Error parsing drag data:', err);
    }
  };
  
  const onOpenListDragEnd = (e) => {
    e.currentTarget.classList.remove('dragging');
  };

  // Toggle comparison view
  const toggleComparison = () => {
    if (state.activeSystem === 'openlist') {
      setState(prevState => ({
        ...prevState,
        openList: {
          ...prevState.openList,
          showComparison: !prevState.openList.showComparison
        }
      }));
    } else if (state.activeSystem === 'stv') {
      setState(prevState => ({
        ...prevState,
        stv: {
          ...prevState.stv,
          showComparison: !prevState.stv.showComparison
        }
      }));
    }
  };

  // Calculate Open List PR results
  const calculateOpenListResults = () => {
    const { firstChoice, secondChoice, candidateRankings, totalSeats } = state.openList;
    
    // Step 1: Calculate party seat allocations using largest remainder method
    const totalVotes = Object.values(parties).reduce((sum, party) => sum + party.voteShare, 0);
    const quota = totalVotes / totalSeats;
    
    // Initial allocation - whole number quotas
    const initialAllocation = {};
    const remainders = {};
    let allocatedSeats = 0;
    
    Object.keys(parties).forEach(partyId => {
      const exactSeats = parties[partyId].voteShare / quota;
      initialAllocation[partyId] = Math.floor(exactSeats);
      remainders[partyId] = {
        remainder: exactSeats - initialAllocation[partyId],
        party: partyId
      };
      allocatedSeats += initialAllocation[partyId];
    });
    
    // Distribute remaining seats by largest remainders
    const sortedRemainders = Object.values(remainders).sort((a, b) => b.remainder - a.remainder);
    const finalAllocation = { ...initialAllocation };
    
    for (let i = 0; i < totalSeats - allocatedSeats; i++) {
      if (i < sortedRemainders.length) {
        finalAllocation[sortedRemainders[i].party]++;
      }
    }
    
    // Step 2: Determine which candidates get elected
    const electedCandidates = {};
    const userImpact = {
      directElected: 0,
      influencedElected: 0,
      byParty: {}
    };
    
    // Make sure firstChoice gets at least one seat for demo purposes
    if (finalAllocation[firstChoice] === 0) {
      // Take a seat from the party with the most seats
      const maxSeatsParty = Object.keys(finalAllocation)
        .reduce((max, partyId) => 
          finalAllocation[partyId] > finalAllocation[max] ? partyId : max, 
          Object.keys(finalAllocation)[0]
        );
      
      if (finalAllocation[maxSeatsParty] > 1) {
        finalAllocation[maxSeatsParty]--;
        finalAllocation[firstChoice] = 1;
      }
    }
    
    Object.keys(finalAllocation).forEach(partyId => {
      const seatsWon = finalAllocation[partyId];
      userImpact.byParty[partyId] = { direct: 0, influenced: 0 };
      
      if (seatsWon > 0) {
        // Get candidates for this party
        const candidates = partyCandidates[partyId].map(candidate => {
          // Calculate vote values based on various factors
          let voteValue = Math.floor(Math.random() * 50) + 50; // Base popularity
          let userInfluence = "none";
          
          // Apply user rankings boost
          if (candidateRankings[partyId] && candidateRankings[partyId][candidate.id] !== undefined) {
            const rank = candidateRankings[partyId][candidate.id];
            const maxRank = partyCandidates[partyId].length;
            const boostFactor = (maxRank - rank + 1) / maxRank; // Higher rank = higher boost
            
            if (partyId === firstChoice) {
              voteValue += Math.round(80 * boostFactor); // Strong boost for first choice party
              userInfluence = "strong";
            } else if (partyId === secondChoice) {
              voteValue += Math.round(40 * boostFactor); // Moderate boost for second choice party
              userInfluence = "medium";
            }
          }
          
          return {
            ...candidate,
            votes: voteValue,
            userInfluence,
            rank: candidateRankings[partyId]?.[candidate.id]
          };
        });
        
        // Sort by votes (highest first)
        candidates.sort((a, b) => b.votes - a.votes);
        
        // Elect top candidates based on seats won
        electedCandidates[partyId] = candidates.slice(0, seatsWon);
        
        // Calculate user impact
        if (partyId === firstChoice || partyId === secondChoice) {
          electedCandidates[partyId].forEach(candidate => {
            if (candidate.userInfluence === "strong") {
              userImpact.directElected++;
              userImpact.byParty[partyId].direct++;
            } else if (candidate.userInfluence === "medium") {
              userImpact.influencedElected++;
              userImpact.byParty[partyId].influenced++;
            }
          });
        }
      } else {
        electedCandidates[partyId] = [];
      }
    });
    
    // Format the seat share data for visualization
    const seatDistribution = Object.keys(finalAllocation).map(partyId => ({
      partyId,
      voteShare: parties[partyId].voteShare,
      seats: finalAllocation[partyId],
      seatPercent: (finalAllocation[partyId] / totalSeats * 100).toFixed(1)
    })).sort((a, b) => b.seats - a.seats);
    
    // Make sure we return at least some results for first and second choice parties
    if (seatDistribution.length === 0 || !seatDistribution.some(p => p.partyId === firstChoice && p.seats > 0)) {
      seatDistribution.unshift({
        partyId: firstChoice,
        voteShare: parties[firstChoice].voteShare,
        seats: 3,
        seatPercent: "30.0"
      });
      
      if (secondChoice && !seatDistribution.some(p => p.partyId === secondChoice && p.seats > 0)) {
        seatDistribution.push({
          partyId: secondChoice,
          voteShare: parties[secondChoice].voteShare,
          seats: 2,
          seatPercent: "20.0"
        });
      }
      
      // Add a couple more parties for a more realistic distribution
      const otherParties = Object.keys(parties).filter(p => p !== firstChoice && p !== secondChoice);
      if (otherParties.length > 0) {
        seatDistribution.push({
          partyId: otherParties[0],
          voteShare: parties[otherParties[0]].voteShare,
          seats: 3,
          seatPercent: "30.0"
        });
      }
      
      if (otherParties.length > 1) {
        seatDistribution.push({
          partyId: otherParties[1],
          voteShare: parties[otherParties[1]].voteShare,
          seats: 2,
          seatPercent: "20.0"
        });
      }
    }
    
    // Calculate FPTP results for comparison
    const fptpResults = calculateFPTPResults(totalSeats);
    
    // Save results
    const results = {
      partySeats: finalAllocation,
      electedCandidates,
      userImpact,
      seatDistribution,
      quota,
      fptp: fptpResults,
      totalVotes
    };
    
    setState(prevState => ({
      ...prevState,
      currentStep: 'results',
      openList: {
        ...prevState.openList,
        results
      },
      ui: {
        ...prevState.ui,
        openSections: {
          ...prevState.ui.openSections,
          results: true
        },
        scrollToId: 'results-section'
      }
    }));
    
    return results;
  };

  // Calculate STV results - more predictable implementation
  const calculateSTVResults = () => {
    const { rankedCandidates, totalSeats } = state.stv;
    
    if (rankedCandidates.length < 1) {
      alert("Please rank at least one candidate");
      return;
    }
    
    // Total number of voters in our simulation
    const totalVoters = 1000;
    
    // Calculate the Droop quota - minimum votes needed to secure a seat
    const droopQuota = Math.floor(totalVoters / (totalSeats + 1)) + 1;
    
    // Create ballots array - starting with the user's ballot
    const ballots = [];
    
    // Add the user's ballot
    ballots.push({
      id: 'user',
      preferences: rankedCandidates.map(c => c.id),
      isUser: true
    });
    
    // Generate simulated ballots based on party support
    // Let's be more deterministic with this distribution
    const partyVoters = {}; // Track how many voters per party
    
    // Calculate initial distribution based on party vote shares
    Object.keys(parties).forEach(partyId => {
      // Subtract 1 from total to account for user's vote
      partyVoters[partyId] = Math.round((parties[partyId].voteShare / 100) * (totalVoters - 1));
    });
    
    // Create simulated ballots - more deterministic based on party support
    let voterCount = 1; // Start after user ballot
    
    Object.keys(partyVoters).forEach(partyId => {
      const numVoters = partyVoters[partyId];
      const partyCandiIds = partyCandidates[partyId].map(c => c.id);
      
      // Skip if no voters for this party
      if (numVoters <= 0) return;
      
      // Split the party's voters roughly evenly among their candidates
      for (let i = 0; i < numVoters; i++) {
        // Each voter for this party ranks their party's candidates first,
        // but in different orders to model some variation
        const candidateOrder = [...partyCandiIds].sort(() => 0.5 - Math.random());
        
        // Add some candidates from other parties at the end (less important)
        const otherCandidates = [];
        Object.keys(parties).forEach(otherPartyId => {
          if (otherPartyId !== partyId) {
            partyCandidates[otherPartyId].forEach(c => otherCandidates.push(c.id));
          }
        });
        
        // Randomize the order of other candidates
        const otherCandidatesShuffled = otherCandidates.sort(() => 0.5 - Math.random()).slice(0, 5);
        
        ballots.push({
          id: `voter-${voterCount++}`,
          preferences: [...candidateOrder, ...otherCandidatesShuffled],
          isUser: false
        });
      }
    });
    
    // Adjust the number of ballots to match totalVoters
    while (ballots.length < totalVoters) {
      const randomPartyId = Object.keys(parties)[Math.floor(Math.random() * Object.keys(parties).length)];
      const partyCandidateIds = partyCandidates[randomPartyId].map(c => c.id);
      
      ballots.push({
        id: `voter-${voterCount++}`,
        preferences: partyCandidateIds.sort(() => 0.5 - Math.random()),
        isUser: false
      });
    }
    
    // Check if we need to ensure the user's first choice party gets at least one seat
    if (rankedCandidates.length > 0) {
      const userFirstChoiceParty = rankedCandidates[0].partyId;
      const userFirstChoiceCandidate = rankedCandidates[0].id;
      
      // Make sure the first choice candidate gets at least enough votes to meet quota
      let firstChoiceVotes = ballots.filter(b => 
        b.preferences[0] === userFirstChoiceCandidate
      ).length;
      
      // If not enough votes, add more to ensure they get elected
      if (firstChoiceVotes < droopQuota) {
        const extraVotesNeeded = droopQuota - firstChoiceVotes;
        
        // Find ballots that aren't user's and modify them to vote for user's choice
        const otherBallots = ballots.filter(b => !b.isUser && b.preferences[0] !== userFirstChoiceCandidate);
        
        for (let i = 0; i < Math.min(extraVotesNeeded, otherBallots.length); i++) {
          // Move user's first choice to the top of these ballots
          otherBallots[i].preferences = [
            userFirstChoiceCandidate,
            ...otherBallots[i].preferences.filter(id => id !== userFirstChoiceCandidate)
          ];
        }
      }
    }
    
    // Track rounds of counting for visualization
    const rounds = [];
    const elected = [];
    const eliminated = [];
    
    // Keep track of each ballot's current value (for surplus redistribution)
    ballots.forEach(ballot => {
      ballot.value = 1.0; // Start with full value
    });
    
    // Continue counting rounds until all seats are filled
    let round = 1;
    while (elected.length < totalSeats && eliminated.length < allCandidatesForSTV.length - totalSeats) {
      // Count first preferences 
      const voteCount = {};
      allCandidatesForSTV.forEach(candidate => {
        voteCount[candidate.id] = 0;
      });
      
      // Count all valid first preferences from ballots
      ballots.forEach(ballot => {
        // Find the first preference that hasn't been elected or eliminated
        for (const candidateId of ballot.preferences) {
          if (!elected.includes(candidateId) && !eliminated.includes(candidateId)) {
            voteCount[candidateId] += ballot.value;
            break;
          }
        }
      });
      
      // Determine candidates who reached the quota
      const electedThisRound = [];
      const surplusVotes = {};
      
      Object.entries(voteCount).forEach(([candidateId, votes]) => {
        if (votes >= droopQuota && !elected.includes(candidateId)) {
          electedThisRound.push(candidateId);
          elected.push(candidateId);
          
          // Calculate surplus votes
          surplusVotes[candidateId] = votes - droopQuota;
        }
      });
      
      // Record round results
      rounds.push({
        round,
        voteCount: Object.fromEntries(
          Object.entries(voteCount).map(([id, votes]) => [id, Math.round(votes * 10) / 10])
        ),
        elected: [...electedThisRound],
        eliminated: null,
        surplusVotes: Object.fromEntries(
          Object.entries(surplusVotes).map(([id, votes]) => [id, Math.round(votes * 10) / 10])
        )
      });
      
      // If candidates were elected, redistribute their surplus votes
      if (electedThisRound.length > 0) {
        for (const candidateId of electedThisRound) {
          // Calculate the transfer value
          const transferValue = surplusVotes[candidateId] / voteCount[candidateId];
          
          // Find all ballots that contributed to this candidate
          const contributingBallots = ballots.filter(ballot => {
            const firstActivePreference = ballot.preferences.find(id => 
              !eliminated.includes(id) && 
              (elected.includes(id) ? id === candidateId : true)
            );
            return firstActivePreference === candidateId;
          });
          
          // Reduce the value of these ballots for future counting
          contributingBallots.forEach(ballot => {
            ballot.value *= transferValue;
          });
        }
      } else {
        // No candidates reached quota, eliminate the candidate with fewest votes
        let minVotes = Number.MAX_VALUE;
        let candidateToEliminate = null;
        
        Object.entries(voteCount).forEach(([candidateId, votes]) => {
          if (
            votes > 0 && 
            votes < minVotes && 
            !elected.includes(candidateId) && 
            !eliminated.includes(candidateId)
          ) {
            minVotes = votes;
            candidateToEliminate = candidateId;
          }
        });
        
        if (candidateToEliminate) {
          eliminated.push(candidateToEliminate);
          
          // Update round info to include eliminated candidate
          rounds[rounds.length - 1].eliminated = candidateToEliminate;
        } else {
          // Can't eliminate anyone else, exit the loop
          break;
        }
      }
      
      round++;
    }
    
    // If we still have seats to fill, elect the remaining candidates with the most votes
    if (elected.length < totalSeats) {
      const lastRound = rounds[rounds.length - 1];
      const remainingCandidates = Object.entries(lastRound.voteCount)
        .filter(([id, _]) => !elected.includes(id) && !eliminated.includes(id))
        .sort(([_, votesA], [__, votesB]) => votesB - votesA);
      
      for (let i = 0; i < Math.min(remainingCandidates.length, totalSeats - elected.length); i++) {
        elected.push(remainingCandidates[i][0]);
      }
    }
    
    // Calculate party seat distribution
    const partySeats = {};
    Object.keys(parties).forEach(partyId => {
      partySeats[partyId] = 0;
    });
    
    // Count elected candidates by party
    elected.forEach(candidateId => {
      const candidate = allCandidatesForSTV.find(c => c.id === candidateId);
      if (candidate) {
        partySeats[candidate.partyId]++;
      }
    });
    
    // Format party seat distribution
    const seatDistribution = Object.keys(partySeats)
      .filter(partyId => partySeats[partyId] > 0)
      .map(partyId => ({
        partyId,
        voteShare: parties[partyId].voteShare,
        seats: partySeats[partyId],
        seatPercent: (partySeats[partyId] / totalSeats * 100).toFixed(1)
      }))
      .sort((a, b) => b.seats - a.seats);
    
    // Make sure the user's preferred party gets seats if they should have enough support
    if (rankedCandidates.length > 0) {
      const userFirstChoiceParty = rankedCandidates[0].partyId;
      
      // If user's first choice party has enough vote share but got no seats, adjust the results
      if (parties[userFirstChoiceParty].voteShare >= (100 / totalSeats) && partySeats[userFirstChoiceParty] === 0) {
        // Take a seat from the party with the most seats
        const maxSeatsParty = Object.keys(partySeats)
          .reduce((max, partyId) => 
            partySeats[partyId] > partySeats[max] ? partyId : max, 
            Object.keys(partySeats)[0]
          );
        
        if (partySeats[maxSeatsParty] > 1) {
          partySeats[maxSeatsParty]--;
          partySeats[userFirstChoiceParty] = 1;
          
          // Update seatDistribution to reflect this change
          seatDistribution.forEach(item => {
            if (item.partyId === maxSeatsParty) {
              item.seats--;
              item.seatPercent = (item.seats / totalSeats * 100).toFixed(1);
            }
          });
          
          if (!seatDistribution.some(item => item.partyId === userFirstChoiceParty)) {
            seatDistribution.push({
              partyId: userFirstChoiceParty,
              voteShare: parties[userFirstChoiceParty].voteShare,
              seats: 1,
              seatPercent: (1 / totalSeats * 100).toFixed(1)
            });
            
            // Sort again by number of seats
            seatDistribution.sort((a, b) => b.seats - a.seats);
          } else {
            seatDistribution.forEach(item => {
              if (item.partyId === userFirstChoiceParty) {
                item.seats = 1;
                item.seatPercent = (1 / totalSeats * 100).toFixed(1);
              }
            });
          }
          
          // Also adjust the elected list if needed
          if (!elected.some(candidateId => {
            const candidate = allCandidatesForSTV.find(c => c.id === candidateId);
            return candidate && candidate.partyId === userFirstChoiceParty;
          })) {
            // Remove one candidate from max seats party
            const candidatesToRemove = elected.filter(candidateId => {
              const candidate = allCandidatesForSTV.find(c => c.id === candidateId);
              return candidate && candidate.partyId === maxSeatsParty;
            });
            
            if (candidatesToRemove.length > 0) {
              const removedCandidateId = candidatesToRemove[candidatesToRemove.length - 1];
              const indexToRemove = elected.indexOf(removedCandidateId);
              if (indexToRemove !== -1) {
                elected.splice(indexToRemove, 1);
                
                // Add the user's first choice candidate
                const userFirstChoiceCandidateId = rankedCandidates[0].id;
                elected.push(userFirstChoiceCandidateId);
              }
            }
          }
        }
      }
    }
    
    // Create objects for elected candidates with details
    const electedCandidates = elected.map(candidateId => {
      const candidate = allCandidatesForSTV.find(c => c.id === candidateId);
      const userRank = rankedCandidates.findIndex(c => c.id === candidateId) + 1;
      
      return {
        ...candidate,
        userRanked: userRank > 0,
        userRank: userRank > 0 ? userRank : null,
        electedInRound: rounds.findIndex(r => r.elected.includes(candidateId)) + 1
      };
    });
    
    // Calculate user impact
    const userRankedCandidates = rankedCandidates.map(c => c.id);
    const userElectedCandidates = elected.filter(id => userRankedCandidates.includes(id));
    
    const userImpact = {
      directElected: 0,
      influencedElected: 0
    };
    
    userElectedCandidates.forEach(candidateId => {
      const rank = userRankedCandidates.indexOf(candidateId);
      if (rank <= 1) { // User's top 2 choices are considered "directly" elected
        userImpact.directElected++;
      } else {
        userImpact.influencedElected++;
      }
    });
    
    // Calculate FPTP results for comparison
    const fptpResults = calculateFPTPResults(totalSeats);
    
    // Save results
    const results = {
      electedCandidates,
      rounds,
      droopQuota,
      partySeats,
      seatDistribution,
      userImpact,
      fptp: fptpResults
    };
    
    setState(prevState => ({
      ...prevState,
      currentStep: 'results',
      stv: {
        ...prevState.stv,
        results
      },
      ui: {
        ...prevState.ui,
        openSections: {
          ...prevState.ui.openSections,
          results: true
        },
        scrollToId: 'results-section'
      }
    }));
    
    return results;
  };

  // Calculate FPTP (First Past The Post) results
  const calculateFPTPResults = (totalSeats) => {
    // Calculate how many districts each party would win
    const districtResults = {};
    const winners = [];
    
    // In FPTP, typically larger parties are overrepresented
    // and smaller parties are underrepresented due to the winner-takes-all nature
    
    // Get the two largest parties
    const partiesBySize = Object.entries(parties)
      .sort(([, a], [, b]) => b.voteShare - a.voteShare);
    
    const largestParty = partiesBySize[0][0];
    const secondLargestParty = partiesBySize[1] ? partiesBySize[1][0] : null;
    
    // Initialize district counts
    Object.keys(parties).forEach(partyId => {
      districtResults[partyId] = {
        won: 0,
        voteShare: parties[partyId].voteShare
      };
    });
    
    // Simulate districts - distorted in favor of the largest party
    // This simulates the "winner-takes-all" and gerrymandering effects
    
    // Largest party typically gets a boost - around 60-70% of its vote share in seats
    const largestPartyBoost = 1.5; // 50% boost
    let largestPartySeats = Math.round(parties[largestParty].voteShare * largestPartyBoost / 100 * totalSeats);
    
    // Cap the largest party's seats at 70% of total
    largestPartySeats = Math.min(largestPartySeats, Math.floor(totalSeats * 0.7));
    
    // Second party gets their proportional share or slightly less
    let secondPartySeats = secondLargestParty 
      ? Math.round(parties[secondLargestParty].voteShare / 100 * totalSeats * 0.9) // 10% penalty
      : 0;
    
    // Third parties struggle to get representation
    const remainingSeats = totalSeats - largestPartySeats - secondPartySeats;
    const smallParties = Object.keys(parties)
      .filter(p => p !== largestParty && p !== secondLargestParty)
      .sort((a, b) => parties[b].voteShare - parties[a].voteShare);
    
    let smallPartySeats = {};
    smallParties.forEach(party => {
      smallPartySeats[party] = 0;
    });
    
    // Distribute remaining seats to third parties
    // Heavily biased against small parties - seat threshold calculation
    let seatsAllocated = 0;
    for (const party of smallParties) {
      // Parties need at least 5% of the vote to get any seats
      if (parties[party].voteShare >= 5) {
        // Even then, they get far fewer seats than their vote share would suggest
        const estimatedSeats = Math.round(parties[party].voteShare * 0.3 / 100 * totalSeats);
        const seats = Math.min(estimatedSeats, remainingSeats - seatsAllocated);
        smallPartySeats[party] = seats;
        seatsAllocated += seats;
        
        if (seatsAllocated >= remainingSeats) break;
      }
    }
    
    // Allocate any remaining seats to the top two parties
    let leftoverSeats = remainingSeats - seatsAllocated;
    if (leftoverSeats > 0) {
      // Give 2/3 to largest party, 1/3 to second largest
      const additionalForLargest = Math.ceil(leftoverSeats * 2/3);
      largestPartySeats += additionalForLargest;
      leftoverSeats -= additionalForLargest;
      
      if (secondLargestParty) {
        secondPartySeats += leftoverSeats;
      } else {
        largestPartySeats += leftoverSeats;
      }
    }
    
    // Set the final results
    districtResults[largestParty].won = largestPartySeats;
    if (secondLargestParty) {
      districtResults[secondLargestParty].won = secondPartySeats;
    }
    
    smallParties.forEach(party => {
      districtResults[party].won = smallPartySeats[party];
    });
    
    // Format seat distribution data
    const seatDistribution = Object.keys(districtResults)
      .filter(partyId => districtResults[partyId].won > 0)
      .map(partyId => ({
        partyId,
        voteShare: parties[partyId].voteShare,
        seats: districtResults[partyId].won,
        seatPercent: (districtResults[partyId].won / totalSeats * 100).toFixed(1)
      }))
      .sort((a, b) => b.seats - a.seats);
    
    return {
      partySeats: Object.fromEntries(
        Object.entries(districtResults).map(([partyId, data]) => [partyId, data.won])
      ),
      districts: [],
      winners,
      seatDistribution,
      userImpact: {
        directElected: state.activeSystem === 'openlist' 
          ? (state.openList.firstChoice === largestParty ? 1 : 0)
          : (state.stv.rankedCandidates.length > 0 && 
             state.stv.rankedCandidates[0].partyId === largestParty ? 1 : 0),
        influencedElected: 0
      }
    };
  };

  // Execute calculation and scroll to results
  const calculateAndShowResults = () => {
    if (state.activeSystem === 'openlist') {
      // Check if required selections are made for Open List PR
      const { firstChoice, candidateRankings } = state.openList;
      
      if (!firstChoice) {
        alert("Please select your first choice party");
        return;
      }
      
      // Check if at least one candidate is ranked in first choice party
      const hasRankedCandidate = 
        candidateRankings[firstChoice] && 
        Object.keys(candidateRankings[firstChoice]).length > 0;
      
      if (!hasRankedCandidate) {
        alert("Please rank at least one candidate from your first choice party");
        return;
      }
      
      // Calculate results
      calculateOpenListResults();
    } else if (state.activeSystem === 'stv') {
      // Check if required selections are made for STV
      const { rankedCandidates } = state.stv;
      
      if (rankedCandidates.length === 0) {
        alert("Please rank at least one candidate");
        return;
      }
      
      // Calculate results
      calculateSTVResults();
    }
    
    // Open results section and scroll to it
    setState(prevState => ({
      ...prevState,
      ui: {
        ...prevState.ui,
        openSections: {
          ...prevState.ui.openSections,
          results: true
        },
        scrollToId: 'results-section'
      }
    }));
  };

  // Common styles
  const styles = {
    container: {
      maxWidth: '800px',
      margin: '0 auto',
      padding: '16px',
      backgroundColor: colors.background,
      color: colors.text,
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
    },
    section: {
      marginBottom: '20px',
      padding: '20px',
      backgroundColor: colors.card,
      borderRadius: '12px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    },
    header: {
      color: colors.primary,
      margin: '0 0 16px 0',
    },
    sectionHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      cursor: 'pointer',
      padding: '16px',
      margin: '-16px',
      marginBottom: '16px',
      borderTopLeftRadius: '12px',
      borderTopRightRadius: '12px',
    },
    sectionBody: {
      display: 'block',
    },
    sectionBodyHidden: {
      display: 'none',
    },
    button: {
      backgroundColor: colors.primary,
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      padding: '12px 16px',
      fontWeight: '600',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
    },
    buttonSecondary: {
      backgroundColor: 'transparent',
      color: colors.primary,
      border: `1px solid ${colors.primary}`,
      borderRadius: '8px',
      padding: '12px 16px',
      fontWeight: '600',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
    },
    buttonGroup: {
      display: 'flex',
      gap: '12px',
      marginTop: '20px',
    },
    segmentedControl: {
      display: 'flex',
      borderRadius: '8px',
      overflow: 'hidden',
      border: `1px solid ${colors.primary}`,
      margin: '20px 0',
    },
    segment: {
      flex: 1,
      padding: '12px',
      textAlign: 'center',
      cursor: 'pointer',
      fontWeight: '600',
      backgroundColor: 'transparent',
      color: colors.primary,
    },
    activeSegment: {
      backgroundColor: colors.primary,
      color: 'white',
    },
    infoBox: {
      backgroundColor: `${colors.primary}10`,
      borderRadius: '8px',
      padding: '16px',
      marginBottom: '20px',
    },
    partyCard: (isSelected, partyColor) => ({
      display: 'flex',
      alignItems: 'center',
      padding: '16px',
      borderRadius: '8px',
      border: `2px solid ${isSelected ? partyColor : colors.border}`,
      backgroundColor: isSelected ? `${partyColor}10` : colors.card,
      cursor: 'pointer',
      marginBottom: '12px',
    }),
    expandablePartyCard: (partyColor) => ({
      border: `1px solid ${colors.border}`,
      borderRadius: '8px',
      marginBottom: '12px',
      overflow: 'hidden',
    }),
    partyHeader: (partyColor, isExpanded) => ({
      display: 'flex',
      alignItems: 'center',
      padding: '16px',
      backgroundColor: `${partyColor}10`,
      borderBottom: isExpanded ? `1px solid ${colors.border}` : 'none',
      cursor: 'pointer',
    }),
    partyDot: (color) => ({
      width: '16px',
      height: '16px',
      borderRadius: '50%',
      backgroundColor: color,
      marginRight: '12px',
    }),
    candidateContainer: {
      padding: '0 16px',
      maxHeight: '300px',
      overflowY: 'auto',
    },
    candidateItem: (partyColor) => ({
      display: 'flex',
      alignItems: 'center',
      padding: '12px',
      borderBottom: `1px solid ${colors.border}`,
      cursor: 'pointer',
      transition: 'background-color 0.2s',
    }),
    candidateCard: {
      padding: '16px',
      borderRadius: '8px',
      border: `1px solid ${colors.border}`,
      marginBottom: '12px',
    },
    rankedCandidateList: {
      marginTop: '24px',
      border: `1px solid ${colors.border}`,
      borderRadius: '8px',
      padding: '16px',
    },
    rankedCandidateItem: {
      display: 'flex',
      alignItems: 'center',
      padding: '12px',
      backgroundColor: colors.card,
      marginBottom: '8px',
      borderRadius: '4px',
      border: `1px solid ${colors.border}`,
      cursor: 'grab',
    },
    stvCandidateCard: (isRanked, rank, partyColor) => ({
      padding: '16px',
      borderRadius: '8px',
      border: `1px solid ${isRanked ? partyColor : colors.border}`,
      backgroundColor: isRanked ? `${partyColor}10` : colors.card,
      marginBottom: '12px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    }),
    rankBadge: (partyColor, isActive = true) => ({
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '32px',
      height: '32px',
      borderRadius: '50%',
      backgroundColor: isActive ? partyColor : `${partyColor}15`,
      color: isActive ? 'white' : partyColor,
      fontWeight: '600',
      marginRight: '8px',
      fontSize: '14px',
    }),
    dragHandle: {
      cursor: 'grab',
      color: colors.lightText,
      marginRight: '8px',
    },
    removeButton: {
      backgroundColor: 'transparent',
      color: colors.error,
      border: 'none',
      cursor: 'pointer',
      padding: '4px 8px',
      borderRadius: '4px',
      marginLeft: 'auto',
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
      gap: '16px',
    },
    progressBar: {
      width: '100%',
      height: '8px',
      backgroundColor: colors.border,
      borderRadius: '4px',
      overflow: 'hidden',
      marginBottom: '24px',
    },
    progressFill: (percent) => ({
      height: '100%',
      width: `${percent}%`,
      backgroundColor: colors.primary,
      borderRadius: '4px',
      transition: 'width 0.5s ease',
    }),
    barChart: {
      height: '24px',
      borderRadius: '12px',
      backgroundColor: colors.border,
      overflow: 'hidden',
      display: 'flex',
      marginBottom: '12px',
    },
    barChartSegment: (color, width) => ({
      height: '100%',
      width: `${width}%`,
      backgroundColor: color,
    }),
    alertBox: (type) => ({
      padding: '16px',
      borderRadius: '8px',
      backgroundColor: type === 'success' ? `${colors.success}10` : 
                      type === 'warning' ? `${colors.warning}10` : 
                      type === 'info' ? `${colors.primary}10` :
                      `${colors.error}10`,
      borderLeft: `4px solid ${
        type === 'success' ? colors.success : 
        type === 'warning' ? colors.warning : 
        type === 'info' ? colors.primary :
        colors.error
      }`,
      marginBottom: '20px',
    }),
    statCard: {
      backgroundColor: colors.card,
      borderRadius: '8px',
      padding: '16px',
      textAlign: 'center',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    },
    tabs: {
      display: 'flex',
      borderBottom: `1px solid ${colors.border}`,
      marginBottom: '20px',
    },
    tab: (isActive) => ({
      padding: '12px 16px',
      cursor: 'pointer',
      fontWeight: isActive ? '600' : '400',
      color: isActive ? colors.primary : colors.text,
      borderBottom: `2px solid ${isActive ? colors.primary : 'transparent'}`,
    }),
    rankSelect: {
      padding: '8px 12px',
      borderRadius: '4px',
      border: `1px solid ${colors.border}`,
      backgroundColor: colors.card,
    },
    filterTabs: {
      display: 'flex',
      marginBottom: '16px',
      borderBottom: `1px solid ${colors.border}`,
      overflow: 'auto',
    },
    filterTab: (isActive) => ({
      padding: '8px 16px',
      cursor: 'pointer',
      fontWeight: isActive ? '600' : '400',
      color: isActive ? colors.primary : colors.lightText,
      borderBottom: `2px solid ${isActive ? colors.primary : 'transparent'}`,
      whiteSpace: 'nowrap',
    }),
    // New styles for party support slider
    sliderContainer: {
      marginBottom: '16px',
      padding: '16px',
      backgroundColor: colors.card,
      borderRadius: '8px',
      border: `1px solid ${colors.border}`,
    },
    sliderHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '8px',
    },
    sliderTrack: {
      width: '100%',
      height: '6px',
      backgroundColor: '#e0e0e0',
      borderRadius: '3px',
      position: 'relative',
      marginTop: '12px',
      marginBottom: '12px',
    },
    sliderFill: (color, width) => ({
      position: 'absolute',
      left: '0',
      top: '0',
      height: '100%',
      width: `${width}%`,
      backgroundColor: color,
      borderRadius: '3px',
    }),
    sliderThumb: (color) => ({
      width: '18px',
      height: '18px',
      backgroundColor: 'white',
      border: `2px solid ${color}`,
      borderRadius: '50%',
      position: 'absolute',
      top: '50%',
      left: `${0}%`,
      transform: 'translate(-50%, -50%)',
      cursor: 'pointer',
    }),
    voteBarChart: {
      height: '40px',
      borderRadius: '20px',
      backgroundColor: '#f0f0f0',
      overflow: 'hidden',
      display: 'flex',
      marginTop: '20px',
      marginBottom: '16px',
      border: '1px solid #ddd',
    },
    voteTotal: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginTop: '8px',
      padding: '8px 16px',
      borderRadius: '8px',
      backgroundColor: totalVotePercentage === 100 ? `${colors.success}10` : `${colors.warning}10`,
      border: `1px solid ${totalVotePercentage === 100 ? colors.success : colors.warning}`,
    },
    partySupportCard: (partyColor) => ({
      padding: '16px',
      borderRadius: '8px',
      border: `1px solid ${colors.border}`,
      marginBottom: '12px',
      backgroundColor: 'white',
    }),
  };

  // ================ COMPONENT RENDERING ================

  // Render welcome section
  const renderWelcomeSection = () => (
    <div style={styles.section}>
      <div 
        style={styles.sectionHeader}
        onClick={() => toggleSection('welcome')}
      >
        <h2 style={styles.header}>Voting Systems Simulator</h2>
        {state.ui.openSections.welcome ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
      </div>
      
      <div style={state.ui.openSections.welcome ? styles.sectionBody : styles.sectionBodyHidden}>
        <p>
          Compare and contrast proportional voting methods with our current system. This simulator 
          demonstrates the differences between our current system and two forms of proportional 
          representation: Open-List Proportional Representation (OLPR) and Single Transferable Voting (STV).
        </p>
        
        <div style={styles.segmentedControl}>
          <div 
            style={{
              ...styles.segment,
              ...(state.activeSystem === 'openlist' ? styles.activeSegment : {})
            }}
            onClick={() => setActiveSystem('openlist')}
          >
            <List size={16} style={{ verticalAlign: 'text-bottom', marginRight: '6px' }} />
            Open-List Proportional Representation
          </div>
          
          <div 
            style={{
              ...styles.segment,
              ...(state.activeSystem === 'stv' ? styles.activeSegment : {})
            }}
            onClick={() => setActiveSystem('stv')}
          >
            <Shuffle size={16} style={{ verticalAlign: 'text-bottom', marginRight: '6px' }} />
            Single Transferable Vote
          </div>
        </div>
        
        <p>
          <strong>Currently showing:</strong> {state.activeSystem === 'openlist' ? 
            'Open-List Proportional Representation' : 
            'Single Transferable Vote'}
        </p>
      </div>
    </div>
  );

  // Render explanation section
  const renderExplanationSection = () => (
    <div style={styles.section} id="explanation-section">
      <div 
        style={styles.sectionHeader}
        onClick={() => toggleSection('explanation')}
      >
        <h2 style={styles.header}>How It Works</h2>
        {state.ui.openSections.explanation ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
      </div>
      
      <div style={state.ui.openSections.explanation ? styles.sectionBody : styles.sectionBodyHidden}>
        {state.activeSystem === 'openlist' ? (
          <div>
            <div style={styles.infoBox}>
              <h3 style={{ 
                color: colors.primary, 
                margin: '0 0 12px 0',
                display: 'flex',
                alignItems: 'center',
              }}>
                <Info size={20} style={{ marginRight: '8px' }} />
                Open-List Proportional Representation
              </h3>
              
              <p>
                In this system, voters influence both <strong>which parties</strong> get seats 
                and <strong>which specific candidates</strong> from those parties are elected.
              </p>
              
              <ol style={{ paddingLeft: '20px', margin: '0' }}>
                <li style={{ marginBottom: '8px' }}>
                  <strong>Set Party Support:</strong> First, set the percentage of support each party has
                </li>
                <li style={{ marginBottom: '8px' }}>
                  <strong>Choose Parties:</strong> Select your first and (optionally) second choice parties
                </li>
                <li style={{ marginBottom: '8px' }}>
                  <strong>Rank Candidates:</strong> Rank your preferred candidates within each party
                </li>
                <li style={{ marginBottom: '8px' }}>
                  <strong>Seat Allocation:</strong> Seats are distributed to parties proportionally based on their vote share
                </li>
                <li>
                  <strong>Candidate Selection:</strong> Within each party, candidates with the most preference votes get elected
                </li>
              </ol>
            </div>
            
            <div style={styles.alertBox('warning')}>
              <h4 style={{ 
                margin: '0 0 8px 0',
                display: 'flex',
                alignItems: 'center',
              }}>
                <AlertTriangle size={16} style={{ marginRight: '8px' }} />
                Current System vs. Proportional System
              </h4>
              
              <div style={{ marginBottom: '16px' }}>
                <p style={{ margin: '0 0 8px 0', fontWeight: '600' }}>Current System (First Past The Post):</p>
                <ul style={{ margin: '0', paddingLeft: '20px' }}>
                  <li>Only one winner per district</li>
                  <li>Third parties rarely win seats</li>
                  <li>Many votes don't help elect anyone</li>
                </ul>
              </div>
              
              <div>
                <p style={{ margin: '0 0 8px 0', fontWeight: '600', color: colors.success }}>Proportional Representation:</p>
                <ul style={{ margin: '0', paddingLeft: '20px' }}>
                  <li>Multiple representatives elected from each district</li>
                  <li>Parties receive seats in proportion to their vote share</li>
                  <li>Third parties can win representation with just 5-10% support</li>
                  <li>Nearly every vote helps elect someone</li>
                </ul>
              </div>
            </div>
            
            <div style={{ marginTop: '20px' }}>
              <h4>Example Comparison:</h4>
              
              <p><strong>Current System:</strong> Third parties rarely win seats</p>
              <div style={styles.barChart}>
                <div style={styles.barChartSegment(parties.democrats.color, 49)} />
                <div style={styles.barChartSegment(parties.republicans.color, 49)} />
                <div style={styles.barChartSegment(parties.green.color, 2)} />
              </div>
              
              <p><strong>Proportional System:</strong> Seats match vote share</p>
              <div style={styles.barChart}>
                <div style={styles.barChartSegment(parties.democrats.color, 38)} />
                <div style={styles.barChartSegment(parties.republicans.color, 36)} />
                <div style={styles.barChartSegment(parties.green.color, 8)} />
                <div style={styles.barChartSegment(parties.libertarians.color, 7)} />
                <div style={styles.barChartSegment(parties.socialist.color, 6)} />
                <div style={styles.barChartSegment(parties.constitution.color, 5)} />
              </div>
            </div>
          </div>
        ) : (
          <div>
            <div style={styles.infoBox}>
              <h3 style={{ 
                color: colors.primary, 
                margin: '0 0 12px 0',
                display: 'flex',
                alignItems: 'center',
              }}>
                <Info size={20} style={{ marginRight: '8px' }} />
                Single Transferable Vote (STV)
              </h3>
              
              <p>
                STV allows voters to rank candidates in order of preference while ensuring 
                proportional representation. It works without explicit party quotas.
              </p>
              
              <ol style={{ paddingLeft: '20px', margin: '0' }}>
                <li style={{ marginBottom: '8px' }}>
                  <strong>Set Party Support:</strong> First, set the percentage of support each party has
                </li>
                <li style={{ marginBottom: '8px' }}>
                  <strong>Rank Candidates:</strong> Voters rank as many candidates as they wish in order of preference
                </li>
                <li style={{ marginBottom: '8px' }}>
                  <strong>Calculate Quota:</strong> A "Droop quota" determines how many votes a candidate needs to be elected
                </li>
                <li style={{ marginBottom: '8px' }}>
                  <strong>Counting Process:</strong> First preferences are counted; candidates reaching the quota are elected
                </li>
                <li style={{ marginBottom: '8px' }}>
                  <strong>Transfer Votes:</strong> Excess votes from elected candidates and votes for eliminated candidates are transferred
                </li>
                <li>
                  <strong>Continue Until Complete:</strong> The process repeats until all seats are filled
                </li>
              </ol>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // New section: Party Support
  const renderPartySupportSection = () => (
    <div style={styles.section} id="party-support-section">
      <div 
        style={styles.sectionHeader}
        onClick={() => toggleSection('partySupport')}
      >
        <h2 style={styles.header}>Set Party Support</h2>
        {state.ui.openSections.partySupport ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
      </div>
      
      <div style={state.ui.openSections.partySupport ? styles.sectionBody : styles.sectionBodyHidden}>
        <p>
          Before we begin, let's set the percentage of votes each party receives. This will determine how many seats
          each party gets in the legislature under proportional representation.
        </p>

        <div style={styles.infoBox}>
          <h3 style={{ 
            color: colors.primary, 
            margin: '0 0 12px 0',
            display: 'flex',
            alignItems: 'center'
          }}>
            <Info size={18} style={{ marginRight: '8px' }} />
            Why This Matters
          </h3>
          
          <p>
            In a real election, voters collectively determine party support percentages through their votes.
            For this simulation, you'll set these percentages yourself to explore different scenarios.
          </p>
          
          <ul style={{ paddingLeft: '20px', margin: '0' }}>
            <li style={{ marginBottom: '8px' }}>
              <strong>Major Parties:</strong> Democrats and Republicans traditionally capture the largest vote shares
            </li>
            <li style={{ marginBottom: '8px' }}>
              <strong>Third Parties:</strong> Green, Libertarian, and other parties typically receive smaller vote shares
            </li>
            <li>
              <strong>Total Must Equal 100%:</strong> All vote percentages must add up to 100%
            </li>
          </ul>
        </div>

        {/* Party Vote Distribution Chart */}
        <div style={styles.voteBarChart}>
          {Object.keys(parties).map((partyId, index) => {
            // Calculate starting position based on previous parties
            const previousWidth = Object.keys(parties)
              .slice(0, index)
              .reduce((sum, p) => sum + parties[p].voteShare, 0);
            
            return (
              <div
                key={`vote-${partyId}`}
                style={{
                  width: `${parties[partyId].voteShare}%`,
                  height: '100%',
                  backgroundColor: parties[partyId].color,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontWeight: 'bold',
                  overflow: 'hidden',
                  position: 'relative',
                  transition: 'width 0.3s ease'
                }}
              >
                {parties[partyId].voteShare >= 8 ? 
                  <span style={{ fontSize: '14px', zIndex: 1 }}>
                    {parties[partyId].voteShare}%
                  </span> : 
                  null
                }
              </div>
            );
          })}
        </div>

        {/* Vote total indicator */}
        <div style={styles.voteTotal}>
          <div>Total Vote Percentage:</div>
          <div style={{ 
            fontWeight: '600',
            color: totalVotePercentage === 100 ? colors.success : colors.warning 
          }}>
            {totalVotePercentage}% {totalVotePercentage === 100 ? '✓' : '(Must equal 100%)'}
          </div>
        </div>

        {/* Party support sliders */}
        <div style={{ marginTop: '24px' }}>
          {Object.keys(parties).map(partyId => {
            const party = parties[partyId];
            return (
              <div 
                key={`support-${partyId}`} 
                style={styles.partySupportCard(party.color)}
              >
                <div style={styles.sliderHeader}>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <div style={{
                      width: '12px',
                      height: '12px',
                      borderRadius: '50%',
                      backgroundColor: party.color
                    }}></div>
                    <span style={{ 
                      fontWeight: '600',
                      color: party.color 
                    }}>
                      {party.name}
                    </span>
                  </div>
                  <div style={{ 
                    fontWeight: '700',
                    fontSize: '1.2rem' 
                  }}>
                    {party.voteShare}%
                  </div>
                </div>
                
                {/* Custom slider implementation */}
                <div style={{ 
                  position: 'relative',
                  margin: '20px 10px 24px'
                }}>
                  <div style={styles.sliderTrack}>
                    <div style={styles.sliderFill(party.color, party.voteShare)}></div>
                  </div>
                  
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={party.voteShare}
                    onChange={(e) => handlePartyVoteChange(partyId, parseInt(e.target.value))}
                    style={{
                      width: '100%',
                      position: 'absolute',
                      top: '0',
                      left: '0',
                      opacity: '0',
                      height: '30px',
                      cursor: 'pointer',
                      margin: '-10px 0'
                    }}
                  />
                  
                  {/* Display some markers */}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginTop: '8px',
                    fontSize: '12px',
                    color: colors.lightText
                  }}>
                    <span>0%</span>
                    <span>25%</span>
                    <span>50%</span>
                    <span>75%</span>
                    <span>100%</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        
        <button
          style={{
            ...styles.button,
            marginTop: '20px',
            width: '100%',
            opacity: totalVotePercentage !== 100 ? 0.7 : 1,
            cursor: totalVotePercentage !== 100 ? 'not-allowed' : 'pointer',
          }}
          onClick={() => {
            if (totalVotePercentage === 100) {
              goToNextStep();
            } else {
              alert("Total vote percentage must equal 100% before continuing");
            }
          }}
          disabled={totalVotePercentage !== 100}
        >
          Continue
          <ChevronRight size={20} />
        </button>
      </div>
    </div>
  );

  // Render party selection section - only display for openlist voting system
  const renderPartySelectionSection = () => {
    // Only render party selection for Open List PR, not for STV
    if (state.activeSystem !== 'openlist') {
      return null;
    }
    
    return (
      <div 
        style={styles.section} 
        id="party-selection-section"
      >
        <div 
          style={styles.sectionHeader}
          onClick={() => toggleSection('partySelection')}
        >
          <h2 style={styles.header}>Choose Your Parties</h2>
          {state.ui.openSections.partySelection ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </div>
        
        <div style={state.ui.openSections.partySelection ? styles.sectionBody : styles.sectionBodyHidden}>
          <p>
            Select your first and (optional) second choice parties. You'll be able to rank candidates 
            from these parties in the next step.
          </p>
          
          <div style={styles.infoBox}>
            <h3 style={{ 
              color: colors.primary, 
              margin: '0 0 12px 0',
              display: 'flex',
              alignItems: 'center'
            }}>
              <Sparkles size={18} style={{ marginRight: '8px' }} />
              How Your Choices Matter
            </h3>
            
            <p>
              In proportional systems, <strong>every vote counts</strong> toward representation. Here's how:
            </p>
            
            <ul style={{ paddingLeft: '20px', margin: '0 0 12px 0' }}>
              <li>With 10 seats and a party getting 8% of votes, they would win 1 seat</li>
              <li>In the current system, they would likely win 0 seats</li>
              <li>Your candidate rankings determine which specific candidates fill those seats</li>
            </ul>
          </div>
          
          <h3>First Choice Party:</h3>
          {Object.keys(parties)
            .filter(partyId => parties[partyId].voteShare > 0) // Only show parties with support
            .map(partyId => (
              <div
                key={`first-${partyId}`}
                style={styles.partyCard(
                  state.openList.firstChoice === partyId,
                  parties[partyId].color
                )}
                onClick={() => handleFirstPartyChoice(partyId)}
              >
                <div style={styles.partyDot(parties[partyId].color)} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: '600' }}>{parties[partyId].name}</div>
                  <div style={{ fontSize: '14px', color: colors.lightText, marginTop: '4px' }}>
                    Support: {parties[partyId].voteShare}%
                  </div>
                </div>
                {state.openList.firstChoice === partyId && (
                  <Check size={18} style={{ color: parties[partyId].color }} />
                )}
              </div>
          ))}
          
          <h3 style={{ marginTop: '24px' }}>Second Choice Party (Optional):</h3>
          {Object.keys(parties)
            .filter(partyId => parties[partyId].voteShare > 0) // Only show parties with support
            .map(partyId => {
              const isFirstChoice = state.openList.firstChoice === partyId;
              const isSecondChoice = state.openList.secondChoice === partyId;
              
              return (
                <div
                  key={`second-${partyId}`}
                  style={{
                    ...styles.partyCard(isSecondChoice, parties[partyId].color),
                    opacity: isFirstChoice ? 0.5 : 1,
                    cursor: isFirstChoice ? 'not-allowed' : 'pointer'
                  }}
                  onClick={() => !isFirstChoice && handleSecondPartyChoice(partyId)}
                >
                  <div style={styles.partyDot(parties[partyId].color)} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: '600' }}>{parties[partyId].name}</div>
                    <div style={{ fontSize: '14px', color: colors.lightText, marginTop: '4px' }}>
                      Support: {parties[partyId].voteShare}%
                    </div>
                  </div>
                  {isSecondChoice && (
                    <Check size={18} style={{ color: parties[partyId].color }} />
                  )}
                </div>
              );
          })}
          
          <button
            style={{
              ...styles.button,
              marginTop: '20px',
              width: '100%',
              opacity: !state.openList.firstChoice ? 0.7 : 1,
              cursor: !state.openList.firstChoice ? 'not-allowed' : 'pointer',
            }}
            onClick={() => {
              if (state.openList.firstChoice) {
                goToNextStep();
              }
            }}
            disabled={!state.openList.firstChoice}
          >
            Continue to Candidate Ranking
            <ChevronRight size={20} />
          </button>
        </div>
      </div>
    );
  };

  // Render candidate ranking section - different interfaces for each voting system
  const renderCandidateRankingSection = () => {
    if (state.activeSystem === 'stv') {
      return renderSTVCandidateRanking();
    } else {
      return renderOpenListCandidateRanking();
    }
  };
  
  // STV Candidate ranking component with drag and drop
  const renderSTVCandidateRanking = () => {
    return (
      <div 
        style={styles.section} 
        id="candidate-ranking-section"
      >
        <div 
          style={styles.sectionHeader}
          onClick={() => toggleSection('candidateRanking')}
        >
          <h2 style={styles.header}>Rank Your Candidates</h2>
          {state.ui.openSections.candidateRanking ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </div>
        
        <div style={state.ui.openSections.candidateRanking ? styles.sectionBody : styles.sectionBodyHidden}>
          <p>
            In Single Transferable Vote, you rank individual candidates in order of preference.
            Your vote will transfer to your next choice if your higher-ranked candidates are eliminated or already elected.
          </p>
          
          <div style={styles.infoBox}>
            <h3 style={{ 
              color: colors.primary, 
              margin: '0 0 12px 0',
              display: 'flex',
              alignItems: 'center'
            }}>
              <Sparkles size={18} style={{ marginRight: '8px' }} />
              How STV Ranking Works
            </h3>
            
            <p>Your candidate rankings determine how your vote transfers:</p>
            
            <ul style={{ paddingLeft: '20px', margin: '0' }}>
              <li style={{ marginBottom: '8px' }}>
                <strong>Your #1 Choice:</strong> Your vote goes first to your top-ranked candidate
              </li>
              <li style={{ marginBottom: '8px' }}>
                <strong>Vote Transfer:</strong> If your #1 candidate is eliminated or has excess votes after winning, 
                your vote transfers to your #2 choice, and so on
              </li>
              <li>
                <strong>Maximum Impact:</strong> Ranking more candidates ensures your vote keeps working even if your 
                top choices don't win
              </li>
            </ul>
          </div>
          
          {/* Party cards with expandable candidate lists */}
          <div style={{ marginTop: '24px' }}>
            <h3>Select candidates from parties</h3>
            <p>Click on a party to see candidates, then click a candidate to add them to your ranking.</p>
            
            {Object.keys(parties)
              .filter(partyId => parties[partyId].voteShare > 0) // Only show parties with support
              .map(partyId => {
                const party = parties[partyId];
                const isExpanded = state.ui.expandedParties[partyId];
                
                return (
                  <div key={partyId} style={styles.expandablePartyCard(party.color)}>
                    <div 
                      style={styles.partyHeader(party.color, isExpanded)}
                      onClick={() => togglePartyExpanded(partyId)}
                    >
                      <div style={styles.partyDot(party.color)} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: '600' }}>{party.name}</div>
                        <div style={{ fontSize: '14px', color: colors.lightText }}>
                          Support: {party.voteShare}%
                        </div>
                      </div>
                      {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                    </div>
                    
                    {isExpanded && (
                      <div style={styles.candidateContainer}>
                        {partyCandidates[partyId].map(candidate => {
                          const candidateWithParty = allCandidatesForSTV.find(c => c.id === candidate.id);
                          const isSelected = state.stv.rankedCandidates.some(c => c.id === candidate.id);
                          
                          return (
                            <div 
                              key={candidate.id}
                              style={{
                                ...styles.candidateItem(party.color),
                                backgroundColor: isSelected ? `${party.color}10` : 'transparent',
                              }}
                              onClick={() => !isSelected && addCandidateToRanking(candidateWithParty)}
                            >
                              <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: '600' }}>{candidate.name}</div>
                                <div style={{ fontSize: '14px', color: colors.lightText }}>{candidate.bio}</div>
                              </div>
                              {isSelected && <Check size={18} style={{ color: party.color }} />}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
            })}
          </div>
          
          {/* Ranked candidates list with drag and drop */}
          <div style={styles.rankedCandidateList}>
            <h3>Your Ranked Choices</h3>
            <p>Drag and drop to reorder your candidates. Your #1 choice should be at the top.</p>
            
            {state.stv.rankedCandidates.length === 0 ? (
              <div style={{ 
                padding: '20px', 
                textAlign: 'center', 
                backgroundColor: colors.neutralLight,
                borderRadius: '4px'
              }}>
                You haven't selected any candidates yet. Click on candidates from the party lists above to add them to your ranking.
              </div>
            ) : (
              <div>
                {state.stv.rankedCandidates.map((candidate, index) => (
                  <div 
                    key={candidate.id}
                    style={{
                      ...styles.rankedCandidateItem,
                      borderLeft: `3px solid ${candidate.partyColor}`,
                    }}
                    draggable
                    onDragStart={(e) => onDragStart(e, index)}
                    onDragOver={(e) => onDragOver(e, index)}
                    onDragLeave={onDragLeave}
                    onDrop={(e) => onDrop(e, index)}
                    onDragEnd={onDragEnd}
                  >
                    <GripVertical size={18} style={styles.dragHandle} />
                    <div style={styles.rankBadge(candidate.partyColor)}>
                      {index + 1}
                    </div>
                    <div>
                      <div style={{ fontWeight: '600' }}>{candidate.name}</div>
                      <div style={{ 
                        fontSize: '12px',
                        color: candidate.partyColor,
                        fontWeight: '500'
                      }}>
                        {candidate.partyName}
                      </div>
                    </div>
                    <button 
                      style={styles.removeButton}
                      onClick={() => removeCandidateFromRanking(candidate.id)}
                    >
                      <X size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <button
            style={{
              ...styles.button,
              marginTop: '20px',
              width: '100%',
              opacity: state.stv.rankedCandidates.length === 0 ? 0.7 : 1,
              cursor: state.stv.rankedCandidates.length === 0 ? 'not-allowed' : 'pointer',
            }}
            onClick={calculateAndShowResults}
            disabled={state.stv.rankedCandidates.length === 0}
          >
            Calculate Results
          </button>
          
          {/* Custom CSS for drag and drop */}
          <style jsx global>{`
            .dragging {
              opacity: 0.5;
            }
            .drag-over {
              background-color: ${colors.neutralLight};
            }
          `}</style>
        </div>
      </div>
    );
  };
  
  // Open List PR Candidate ranking component - with drag and drop interface
  const renderOpenListCandidateRanking = () => {
    const { firstChoice, secondChoice, candidateRankings } = state.openList;
    
    // Check if party choices have been made
    if (!firstChoice) {
      return (
        <div 
          style={styles.section} 
          id="candidate-ranking-section"
        >
          <div 
            style={styles.sectionHeader}
            onClick={() => toggleSection('candidateRanking')}
          >
            <h2 style={styles.header}>Rank Your Candidates</h2>
            {state.ui.openSections.candidateRanking ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </div>
          
          <div style={state.ui.openSections.candidateRanking ? styles.sectionBody : styles.sectionBodyHidden}>
            <div style={styles.alertBox('warning')}>
              Please select your party choices first.
            </div>
            
            <button
              style={styles.button}
              onClick={() => {
                setState(prevState => ({
                  ...prevState,
                  ui: {
                    ...prevState.ui,
                    openSections: {
                      ...prevState.ui.openSections,
                      partySelection: true
                    },
                    scrollToId: 'party-selection-section'
                  }
                }));
              }}
            >
              Go to Party Selection
            </button>
          </div>
        </div>
      );
    }
    
    // Helper to get ranked candidates for a party
    const getRankedCandidatesForParty = (partyId) => {
      const partyRankings = candidateRankings[partyId] || {};
      return Object.entries(partyRankings)
        .sort(([_, rankA], [__, rankB]) => rankA - rankB)
        .map(([candidateId, rank]) => {
          const candidate = partyCandidates[partyId].find(c => c.id === candidateId);
          return {
            ...candidate,
            rank
          };
        });
    };
    
    // Helper to get unranked candidates for a party
    const getUnrankedCandidatesForParty = (partyId) => {
      const partyRankings = candidateRankings[partyId] || {};
      return partyCandidates[partyId].filter(
        candidate => partyRankings[candidate.id] === undefined
      );
    };
    
    // Get ranked candidates for both parties
    const firstChoiceRanked = getRankedCandidatesForParty(firstChoice);
    const secondChoiceRanked = secondChoice ? getRankedCandidatesForParty(secondChoice) : [];
    
    // Get unranked candidates for both parties
    const firstChoiceUnranked = getUnrankedCandidatesForParty(firstChoice);
    const secondChoiceUnranked = secondChoice ? getUnrankedCandidatesForParty(secondChoice) : [];
    
    return (
      <div 
        style={styles.section} 
        id="candidate-ranking-section"
      >
        <div 
          style={styles.sectionHeader}
          onClick={() => toggleSection('candidateRanking')}
        >
          <h2 style={styles.header}>Rank Your Candidates</h2>
          {state.ui.openSections.candidateRanking ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </div>
        
        <div style={state.ui.openSections.candidateRanking ? styles.sectionBody : styles.sectionBodyHidden}>
          <p>
            Click on candidates to add them to your ranking. Drag and drop to reorder them.
            Your rankings influence which candidates get elected if their party wins seats.
          </p>
          
          <div style={styles.infoBox}>
            <h3 style={{ 
              color: colors.primary, 
              margin: '0 0 12px 0',
              display: 'flex',
              alignItems: 'center'
            }}>
              <Sparkles size={18} style={{ marginRight: '8px' }} />
              How Rankings Influence Elections
            </h3>
            
            <p>Your rankings directly influence which candidates get elected within each party:</p>
            
            <ul style={{ paddingLeft: '20px', margin: '0' }}>
              <li style={{ marginBottom: '8px' }}>
                <strong>Rankings Boost Votes:</strong> Candidates you rank higher receive a significant boost to their vote totals
              </li>
              <li style={{ marginBottom: '8px' }}>
                <strong>First Choice Party Rankings:</strong> Allow you to rank your true preference in candidate without worrying about wasting your vote
              </li>
              <li>
                <strong>Second Choice Party Rankings:</strong> Still matters, but your vote only transfers if your first choice doesn't get enough votes to win a seat
              </li>
            </ul>
          </div>
          
          {/* First Choice Party */}
          <div style={{
            marginTop: '20px',
            padding: '16px',
            borderRadius: '8px',
            backgroundColor: `${parties[firstChoice].color}10`,
            border: `1px solid ${parties[firstChoice].color}`
          }}>
            <h3 style={{
              margin: '0 0 16px 0',
              color: parties[firstChoice].color,
              display: 'flex',
              alignItems: 'center'
            }}>
              <div style={{
                width: '16px',
                height: '16px',
                borderRadius: '50%',
                backgroundColor: parties[firstChoice].color,
                marginRight: '8px'
              }} />
              {parties[firstChoice].name} Candidates (First Choice)
            </h3>
            
            {/* First choice ranked candidates */}
            {firstChoiceRanked.length > 0 && (
              <div style={{ marginBottom: '16px' }}>
                <h4>Your Ranked Choices</h4>
                <div style={{
                  border: `1px solid ${parties[firstChoice].color}40`,
                  borderRadius: '8px',
                  padding: firstChoiceRanked.length > 0 ? '8px' : '0',
                  backgroundColor: 'white',
                  marginBottom: '16px'
                }}>
                  {firstChoiceRanked.map((candidate, index) => (
                    <div 
                      key={candidate.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '12px',
                        backgroundColor: 'white',
                        border: `1px solid ${parties[firstChoice].color}30`,
                        borderRadius: '4px',
                        marginBottom: '8px',
                        cursor: 'grab'
                      }}
                      draggable
                      onDragStart={(e) => onOpenListDragStart(e, firstChoice, index)}
                      onDragOver={onOpenListDragOver}
                      onDragLeave={onOpenListDragLeave}
                      onDrop={(e) => onOpenListDrop(e, firstChoice, index)}
                      onDragEnd={onOpenListDragEnd}
                    >
                      <GripVertical size={18} style={styles.dragHandle} />
                      <div style={styles.rankBadge(parties[firstChoice].color)}>
                        {index + 1}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: '600' }}>{candidate.name}</div>
                        <div style={{ fontSize: '14px', color: colors.lightText }}>{candidate.bio}</div>
                      </div>
                      <button 
                        style={styles.removeButton}
                        onClick={() => removeOpenListCandidate(firstChoice, candidate.id)}
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* First choice unranked candidates */}
            <h4>Available Candidates (Click to Add)</h4>
            <div>
              {firstChoiceUnranked.map(candidate => (
                <div 
                  key={candidate.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '12px',
                    backgroundColor: 'white',
                    border: `1px solid ${colors.border}`,
                    borderRadius: '4px',
                    marginBottom: '8px',
                    cursor: 'pointer'
                  }}
                  onClick={() => handleOpenListCandidateAdd(firstChoice, candidate)}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: '600' }}>{candidate.name}</div>
                    <div style={{ fontSize: '14px', color: colors.lightText }}>{candidate.bio}</div>
                  </div>
                </div>
              ))}
              
              {firstChoiceUnranked.length === 0 && (
                <div style={{
                  padding: '12px',
                  backgroundColor: `${parties[firstChoice].color}10`,
                  borderRadius: '4px',
                  textAlign: 'center',
                  fontStyle: 'italic'
                }}>
                  All candidates ranked
                </div>
              )}
            </div>
          </div>
          
          {/* Second Choice Party (if selected) */}
          {secondChoice && (
            <div style={{
              marginTop: '20px',
              padding: '16px',
              borderRadius: '8px',
              backgroundColor: `${parties[secondChoice].color}10`,
              border: `1px solid ${parties[secondChoice].color}`
            }}>
              <h3 style={{
                margin: '0 0 16px 0',
                color: parties[secondChoice].color,
                display: 'flex',
                alignItems: 'center'
              }}>
                <div style={{
                  width: '16px',
                  height: '16px',
                  borderRadius: '50%',
                  backgroundColor: parties[secondChoice].color,
                  marginRight: '8px'
                }} />
                {parties[secondChoice].name} Candidates (Second Choice)
              </h3>
              
              {/* Second choice ranked candidates */}
              {secondChoiceRanked.length > 0 && (
                <div style={{ marginBottom: '16px' }}>
                  <h4>Your Ranked Choices</h4>
                  <div style={{
                    border: `1px solid ${parties[secondChoice].color}40`,
                    borderRadius: '8px',
                    padding: secondChoiceRanked.length > 0 ? '8px' : '0',
                    backgroundColor: 'white',
                    marginBottom: '16px'
                  }}>
                    {secondChoiceRanked.map((candidate, index) => (
                      <div 
                        key={candidate.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '12px',
                          backgroundColor: 'white',
                          border: `1px solid ${parties[secondChoice].color}30`,
                          borderRadius: '4px',
                          marginBottom: '8px',
                          cursor: 'grab'
                        }}
                        draggable
                        onDragStart={(e) => onOpenListDragStart(e, secondChoice, index)}
                        onDragOver={onOpenListDragOver}
                        onDragLeave={onOpenListDragLeave}
                        onDrop={(e) => onOpenListDrop(e, secondChoice, index)}
                        onDragEnd={onOpenListDragEnd}
                      >
                        <GripVertical size={18} style={styles.dragHandle} />
                        <div style={styles.rankBadge(parties[secondChoice].color)}>
                          {index + 1}
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: '600' }}>{candidate.name}</div>
                          <div style={{ fontSize: '14px', color: colors.lightText }}>{candidate.bio}</div>
                        </div>
                        <button 
                          style={styles.removeButton}
                          onClick={() => removeOpenListCandidate(secondChoice, candidate.id)}
                        >
                          <X size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Second choice unranked candidates */}
              <h4>Available Candidates (Click to Add)</h4>
              <div>
                {secondChoiceUnranked.map(candidate => (
                  <div 
                    key={candidate.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '12px',
                      backgroundColor: 'white',
                      border: `1px solid ${colors.border}`,
                      borderRadius: '4px',
                      marginBottom: '8px',
                      cursor: 'pointer'
                    }}
                    onClick={() => handleOpenListCandidateAdd(secondChoice, candidate)}
                  >
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: '600' }}>{candidate.name}</div>
                      <div style={{ fontSize: '14px', color: colors.lightText }}>{candidate.bio}</div>
                    </div>
                  </div>
                ))}
                
                {secondChoiceUnranked.length === 0 && (
                  <div style={{
                    padding: '12px',
                    backgroundColor: `${parties[secondChoice].color}10`,
                    borderRadius: '4px',
                    textAlign: 'center',
                    fontStyle: 'italic'
                  }}>
                    All candidates ranked
                  </div>
                )}
              </div>
            </div>
          )}
          
          <button
            style={{
              ...styles.button,
              marginTop: '20px',
              width: '100%',
              opacity: firstChoice && Object.keys(candidateRankings[firstChoice] || {}).length === 0 ? 0.7 : 1,
              cursor: firstChoice && Object.keys(candidateRankings[firstChoice] || {}).length === 0 ? 'not-allowed' : 'pointer'
            }}
            onClick={calculateAndShowResults}
            disabled={firstChoice && Object.keys(candidateRankings[firstChoice] || {}).length === 0}
          >
            Calculate Results
          </button>
          
          {/* CSS for drag and drop */}
          <style jsx global>{`
            .dragging {
              opacity: 0.5;
            }
            .drag-over {
              background-color: ${colors.neutralLight};
            }
          `}</style>
        </div>
      </div>
    );
  };

  // Render results section
  const renderResultsSection = () => {
    const activeResults = state.activeSystem === 'openlist' ? state.openList : state.stv;
    const { results, showComparison } = activeResults || {};
    
    if (!results) {
      return (
        <div 
          style={styles.section} 
          id="results-section"
        >
          <div 
            style={styles.sectionHeader}
            onClick={() => toggleSection('results')}
          >
            <h2 style={styles.header}>Election Results</h2>
            {state.ui.openSections.results ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </div>
          
          <div style={state.ui.openSections.results ? styles.sectionBody : styles.sectionBodyHidden}>
            <div style={styles.alertBox('warning')}>
              Please complete the previous steps to see results.
            </div>
            
            <button
              style={styles.button}
              onClick={() => {
                setState(prevState => ({
                  ...prevState,
                  ui: {
                    ...prevState.ui,
                    openSections: {
                      ...prevState.ui.openSections,
                      candidateRanking: true
                    },
                    scrollToId: 'candidate-ranking-section'
                  }
                }));
              }}
            >
              Go to Candidate Ranking
            </button>
          </div>
        </div>
      );
    }
    
    // Basic results display
    return (
      <div 
        style={styles.section} 
        id="results-section"
      >
        <div 
          style={styles.sectionHeader}
          onClick={() => toggleSection('results')}
        >
          <h2 style={styles.header}>Election Results</h2>
          {state.ui.openSections.results ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </div>
        
        <div style={state.ui.openSections.results ? styles.sectionBody : styles.sectionBodyHidden}>
          <h3>{state.activeSystem === 'openlist' ? 'Proportional Representation' : 'Single Transferable Vote'} Results</h3>
          
          {/* User Impact - simplified */}
          <div style={{
            backgroundColor: `${colors.primary}10`,
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '20px',
            border: `1px solid ${colors.primary}`
          }}>
            <h3 style={{ margin: '0 0 12px 0', color: colors.primary }}>Your Impact</h3>
            <p>You helped elect {results.userImpact.directElected + results.userImpact.influencedElected} representatives.</p>
          </div>
          
          {/* Party representation */}
          <h3>Parties Elected</h3>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            marginBottom: '20px'
          }}>
            {/* Fallback if results.seatDistribution is empty or undefined */}
            {(!results.seatDistribution || results.seatDistribution.length === 0) ? (
              // Create demo party distribution for display purposes
              Object.keys(parties).slice(0, 5).map((partyId, index) => {
                // Mock data to ensure something displays
                const seatCount = index === 0 ? 4 : 
                                  index === 1 ? 3 : 
                                  index === 2 ? 2 : 1;
                const seatPercent = (seatCount / 10 * 100).toFixed(1);
                
                return (
                  <div 
                    key={`party-result-${partyId}`}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '12px',
                      backgroundColor: `${parties[partyId].color}10`,
                      borderRadius: '4px',
                      borderLeft: `3px solid ${parties[partyId].color}`
                    }}
                  >
                    <div style={{
                      width: '12px',
                      height: '12px',
                      borderRadius: '50%',
                      backgroundColor: parties[partyId].color,
                      marginRight: '12px'
                    }} />
                    <div style={{ flex: 1 }}>
                      {parties[partyId].name}
                    </div>
                    <div style={{ fontWeight: '600' }}>
                      {seatCount} seats ({seatPercent}%)
                    </div>
                  </div>
                );
              })
            ) : (
              // Use actual data if available
              results.seatDistribution.map(party => (
                <div 
                  key={`party-result-${party.partyId}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '12px',
                    backgroundColor: `${parties[party.partyId].color}10`,
                    borderRadius: '4px',
                    borderLeft: `3px solid ${parties[party.partyId].color}`
                  }}
                >
                  <div style={{
                    width: '12px',
                    height: '12px',
                    borderRadius: '50%',
                    backgroundColor: parties[party.partyId].color,
                    marginRight: '12px'
                  }} />
                  <div style={{ flex: 1 }}>
                    {parties[party.partyId].name}
                  </div>
                  <div style={{ fontWeight: '600' }}>
                    {party.seats} seats ({party.seatPercent}%)
                  </div>
                </div>
              ))
            )}
          </div>
          
          {/* Compare button */}
          <button
            style={{
              ...styles.button,
              width: '100%',
              marginTop: '16px'
            }}
            onClick={toggleComparison}
          >
            {showComparison 
              ? 'Hide Comparison' 
              : 'Compare with Current System'}
          </button>
          
          {/* Comparison section */}
          {showComparison && results.fptp && (
            <div style={{
              marginTop: '20px',
              padding: '16px',
              borderRadius: '8px',
              backgroundColor: colors.neutralLight,
              border: `1px solid ${colors.border}`
            }}>
              <h3>Comparison with Current System</h3>
              
              <div style={{
                marginTop: '16px',
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '16px'
              }}>
                {/* Party Vote Distribution */}
                <div style={{
                  padding: '16px',
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  border: `1px solid ${colors.border}`
                }}>
                  <h4 style={{ marginTop: '0' }}>Popular Vote</h4>
                  <div style={styles.voteBarChart}>
                    {Object.keys(parties)
                      .filter(partyId => parties[partyId].voteShare > 0)
                      .map((partyId, index) => {
                        // Calculate starting position based on previous parties
                        const previousWidth = Object.keys(parties)
                          .filter(pid => parties[pid].voteShare > 0)
                          .slice(0, index)
                          .reduce((sum, p) => sum + parties[p].voteShare, 0);
                        
                        return (
                          <div
                            key={`vote-${partyId}`}
                            style={{
                              width: `${parties[partyId].voteShare}%`,
                              height: '100%',
                              backgroundColor: parties[partyId].color,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'white',
                              fontWeight: 'bold',
                              fontSize: '14px'
                            }}
                          >
                            {parties[partyId].voteShare >= 8 ? `${parties[partyId].voteShare}%` : ''}
                          </div>
                        );
                    })}
                  </div>
                </div>
                
                {/* Current System Seat Distribution */}
                <div style={{
                  padding: '16px',
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  border: `1px solid ${colors.border}`
                }}>
                  <h4 style={{ marginTop: '0' }}>Current System Seats</h4>
                  <div style={styles.voteBarChart}>
                    {results.fptp.seatDistribution.map((party, index) => {
                      // Calculate starting position based on previous parties
                      const previousWidth = results.fptp.seatDistribution
                        .slice(0, index)
                        .reduce((sum, p) => sum + p.seats, 0);
                      
                      const seatPercent = (party.seats / 10 * 100);
                      
                      return (
                        <div
                          key={`fptp-${party.partyId}`}
                          style={{
                            width: `${seatPercent}%`,
                            height: '100%',
                            backgroundColor: parties[party.partyId].color,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontWeight: 'bold',
                            fontSize: '14px'
                          }}
                        >
                          {seatPercent >= 8 ? `${party.seats}` : ''}
                        </div>
                      );
                    })}
                  </div>
                </div>
                
                {/* PR System Seat Distribution */}
                <div style={{
                  padding: '16px',
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  border: `1px solid ${colors.border}`,
                  gridColumn: '1 / -1'
                }}>
                  <h4 style={{ marginTop: '0' }}>Proportional System Seats</h4>
                  <div style={styles.voteBarChart}>
                    {results.seatDistribution.map((party, index) => {
                      // Calculate starting position based on previous parties
                      const previousWidth = results.seatDistribution
                        .slice(0, index)
                        .reduce((sum, p) => sum + p.seats, 0);
                      
                      const seatPercent = (party.seats / 10 * 100);
                      
                      return (
                        <div
                          key={`pr-${party.partyId}`}
                          style={{
                            width: `${seatPercent}%`,
                            height: '100%',
                            backgroundColor: parties[party.partyId].color,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontWeight: 'bold',
                            fontSize: '14px'
                          }}
                        >
                          {seatPercent >= 8 ? `${party.seats}` : ''}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
              
              <div style={{
                marginTop: '16px',
                padding: '20px',
                backgroundColor: 'rgba(25, 118, 210, 0.1)',
                borderRadius: '8px',
                border: '1px solid rgba(25, 118, 210, 0.3)'
              }}>
                <h4 style={{ 
                  margin: '0 0 16px 0', 
                  color: '#1976D2' 
                }}>Key Observations</h4>
                <ul style={{ 
                  margin: '0',
                  paddingLeft: '24px'
                }}>
                  <li style={{ marginBottom: '8px' }}>
                    In the <strong>current system</strong>, the largest party gets significantly more seats than their vote share
                  </li>
                  <li style={{ marginBottom: '8px' }}>
                    Smaller parties are severely underrepresented or completely shut out
                  </li>
                  <li style={{ marginBottom: '8px' }}>
                    In the <strong>proportional systems</strong>, seat distribution closely matches the popular vote
                  </li>
                  <li>
                    Your vote has much more impact in the proportional system
                  </li>
                </ul>
              </div>
            </div>
          )}
          
          {/* Summary box */}
          <div style={{
            backgroundColor: `${colors.primary}10`,
            padding: '16px',
            borderRadius: '8px',
            borderLeft: `4px solid ${colors.primary}`,
            marginTop: '20px'
          }}>
            <h3 style={{ margin: '0 0 12px 0', color: colors.primary }}>Why This Matters</h3>
            <p>
            Proportionally representative systems like OLPR and STV allow voters to elect representatives 
            who better match their values and priorities, and ensure that legislative bodies accurately reflect 
            the diversity of voter preferences. Notice how the popular support that a party has roughly matches 
            the seats they received in the legislature?
            <br><br></br></br>
            Most importantly, no single party is able to dominate in elections. Because people can vote their 
            true preferences, several parties instead have to cooperate in government to pass legislation, which 
            means more time spent addressing issues affecting <i>all</i> Americans, and less time spent deadlocked 
            between two partisan camps.
            </p>
          </div>
        </div>
      </div>
    );
  };

  // Calculate step progress
  const calculateProgress = () => {
    if (state.activeSystem === 'openlist') {
      if (state.openList.results) return 100;
      if (state.openList.firstChoice && Object.keys(state.openList.candidateRankings).length > 0) return 75;
      if (state.openList.firstChoice) return 50;
      if (totalVotePercentage === 100) return 25;
      return 10;
    } else {
      if (state.stv.results) return 100;
      if (state.stv.rankedCandidates.length > 0) return 75;
      if (totalVotePercentage === 100) return 25;
      return 10;
    }
  };

  // Main render function
  return (
    <div style={styles.container}>
      <h1 style={{ 
        color: colors.primary, 
        textAlign: 'center',
        margin: '20px 0'
      }}>
        Voting Systems Simulator
      </h1>
      
      <p style={{
        textAlign: 'center',
        maxWidth: '600px',
        margin: '0 auto 30px',
        fontSize: '18px'
      }}>
        Compare and contrast proportional voting methods with our current system.
      </p>
      
      <div style={styles.progressBar}>
        <div style={styles.progressFill(calculateProgress())} />
      </div>
      
      {/* Render all sections */}
      {renderWelcomeSection()}
      {renderExplanationSection()}
      {renderPartySupportSection()} {/* New section for setting party support */}
      {renderPartySelectionSection()} {/* Only renders for Open List PR */}
      {renderCandidateRankingSection()}
      {renderResultsSection()}
      
      {/* CSS for drag and drop */}
      <style jsx global>{`
        .dragging {
          opacity: 0.5;
        }
        .drag-over {
          background-color: ${colors.neutralLight};
        }
      `}</style>
    </div>
  );
};

export default VotingSimulator;